/**
 * AdminDedupPage — Phase 2A Item 5 (2026-09-03).
 *
 * Page admin dédiée à la déduplication du catalogue de postes BTP.
 * Alternative UX au curl 2 étapes (dry-run + exec avec confirm token) pour iPad.
 *
 * États UI :
 *   - idle : bouton "Simuler la déduplication" seul
 *   - simulating / executing : spinner
 *   - plan_ready : plan affiché (2 sections : homogènes fusionnables, hétérogènes bloquants)
 *   - executed : résultat + désactivation temporaire du bouton exec
 *   - error : message d'erreur
 *
 * Décision produit (user 2026-09-03) : ré-exécution AUTORISÉE (option ii).
 * Le dedup est légitimement récurrent (après re-imports). Chaque exec logge
 * une nouvelle ligne dans `catalog_dedup_runs`.
 *
 * Règle produit : le confirm token `YES-DEDUP-<tenant_slug>` est géré côté
 * frontend et envoyé automatiquement au backend. Le user n'a pas à le taper.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Play,
  ShieldAlert,
  Search,
  XCircle,
} from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";

export default function AdminDedupPage() {
  const [status, setStatus] = useState("idle");
  const [plan, setPlan] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [error, setError] = useState("");
  const { state } = useAuth();

  // Slug tenant — récupéré depuis /company/settings pour construire le confirm token
  const [tenantSlug, setTenantSlug] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/company/settings");
        setTenantSlug(data.slug || "");
      } catch (e) {
        // pas bloquant : on inférera au moment de l'exec
      }
    })();
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  const runDryRun = async () => {
    setStatus("simulating");
    setError("");
    setPlan(null);
    setExecResult(null);
    try {
      const { data } = await api.post("/catalog/dedup", { dry_run: true });
      setPlan(data);
      setStatus("plan_ready");
    } catch (e) {
      setError(formatApiError(e));
      setStatus("error");
    }
  };

  const runExec = async () => {
    if (!tenantSlug) {
      toast.error("Slug tenant introuvable — impossible de construire le confirm token.");
      return;
    }
    setStatus("executing");
    setError("");
    try {
      const { data } = await api.post("/catalog/dedup", {
        dry_run: false,
        confirm: `YES-DEDUP-${tenantSlug}`,
      });
      setExecResult(data);
      setStatus("executed");
      toast.success(
        `Déduplication exécutée : ${data.removed} posts supprimés, ${data.remapped_lines} lignes remappées.`,
      );
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data;
      if (status === 409) {
        const het = body?.detail?.heterogeneous_groups || [];
        toast.warning(
          `Fusion refusée : ${het.length} groupe(s) hétérogène(s) à patcher manuellement d'abord.`,
        );
        setError(
          `Groupes hétérogènes détectés au moment de l'exécution. Relance la simulation.`,
        );
      } else {
        setError(formatApiError(e));
      }
      setStatus("error");
    }
  };

  const homogeneousGroups = (plan?.groups || []).filter((g) => g.removed_ids?.length > 0);
  const heterogeneousGroups = plan?.heterogeneous_groups || [];
  const hasHeterogeneous = heterogeneousGroups.length > 0;
  const hasHomogeneous = homogeneousGroups.length > 0;
  // Fix WARN 2026-09-03 : bouton "Exécuter" visible UNIQUEMENT si un plan
  // fusionnable existe (homogeneous > 0) ET aucun blocage hétérogène.
  // Un `simulate` qui retourne "aucun doublon" doit cacher le bouton exec.
  const canExec = status === "plan_ready" && hasHomogeneous && !hasHeterogeneous;
  const showExecButton = status !== "idle" && (hasHomogeneous || hasHeterogeneous);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6" data-testid="admin-dedup-page">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
          <ShieldAlert className="h-4 w-4" />
          Administration
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100 sm:text-3xl">
              Déduplication du catalogue
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Détecte et fusionne les doublons du catalogue (même libellé + même prix).
              Simulation obligatoire avant exécution. Les groupes avec variantes
              divergentes (unité, TVA) sont automatiquement bloqués.
            </p>
          </div>
          <Badge className="badge-neutral self-start sm:self-auto">
            Tenant&nbsp;: {(tenantSlug || "?").toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Actions */}
      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="text-sm text-slate-300">
            {status === "idle" &&
              "Cliquez sur « Simuler la déduplication » pour voir le plan de fusion sans écrire en base."}
            {status === "plan_ready" && !hasHeterogeneous &&
              "Plan validé : aucun groupe hétérogène. Vous pouvez exécuter la fusion."}
            {status === "plan_ready" && hasHeterogeneous &&
              `⚠ ${heterogeneousGroups.length} groupe(s) hétérogène(s) détecté(s). Corrigez-les manuellement (PATCH catalog_items) avant d'exécuter.`}
            {status === "executed" && execResult &&
              `✅ ${execResult.removed} postes supprimés, ${execResult.remapped_lines} lignes remappées.`}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              onClick={runDryRun}
              disabled={status === "simulating" || status === "executing"}
              variant="outline"
              className="h-14 min-w-[220px] gap-2 border-amber-800/60 bg-amber-950/30 text-amber-100 hover:bg-amber-900/40 disabled:opacity-40"
              data-testid="admin-dedup-simulate-btn"
            >
              {status === "simulating" ? (
                <><Loader2 className="h-5 w-5 animate-spin" /> Simulation…</>
              ) : (
                <><Search className="h-5 w-5" /> Simuler la déduplication</>
              )}
            </Button>
            {showExecButton && (
              <Button
                onClick={runExec}
                disabled={!canExec || status === "executing"}
                className="h-14 min-w-[220px] gap-2 bg-[#C4553B] text-white hover:bg-[#B04930] disabled:opacity-40"
                data-testid="admin-dedup-execute-btn"
              >
                {status === "executing" ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Exécution…</>
                ) : (
                  <><Play className="h-5 w-5" /> Exécuter la déduplication</>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Erreur */}
      {status === "error" && error && (
        <Card className="border-red-800 bg-red-950/30">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
            <div>
              <div className="font-medium text-red-300">Erreur</div>
              <div className="mt-1 text-sm text-red-400/80">{error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Groupes hétérogènes (bloquants) */}
      {heterogeneousGroups.length > 0 && (
        <Card className="border-red-900/40 bg-red-950/20" data-testid="admin-dedup-heterogeneous-block">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 border-b border-red-900/40 pb-3">
              <XCircle className="h-5 w-5 text-red-400" />
              <h2 className="font-semibold text-red-200">
                Groupes hétérogènes — fusion refusée ({heterogeneousGroups.length})
              </h2>
            </div>
            <p className="pt-3 text-sm text-red-300/80">
              Ces groupes ont un libellé et un prix identiques mais des variantes
              divergentes (unité, TVA). PATCH manuellement les variantes légitimes
              avant de relancer.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-red-400/70">
                  <tr>
                    <th className="pb-2 pr-4 text-left font-medium">Libellé</th>
                    <th className="pb-2 pr-4 text-right font-medium">Prix</th>
                    <th className="pb-2 pr-4 text-left font-medium">Variantes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-900/40">
                  {heterogeneousGroups.map((g, i) => (
                    <tr key={i}>
                      <td className="py-2 pr-4 text-red-100">{g.label}</td>
                      <td className="py-2 pr-4 text-right text-red-200">
                        {g.unit_price != null ? formatCurrencyBE(g.unit_price) : "—"}
                      </td>
                      <td className="py-2 pr-4 text-red-200">
                        {(g.variants || []).map((v, j) => (
                          <div key={j} className="text-xs">
                            id {v.id?.slice(0, 8)}… · unit=<b>{v.unit || "—"}</b> · TVA=<b>{v.default_vat_rate ?? "—"}%</b>
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Groupes homogènes (fusionnables) */}
      {homogeneousGroups.length > 0 && (
        <Card className="border-slate-800 bg-slate-900/40" data-testid="admin-dedup-homogeneous-block">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              <h2 className="font-semibold text-slate-100">
                Groupes fusionnables ({homogeneousGroups.length})
              </h2>
              <Badge className="badge-neutral">
                {plan?.removed_simulated ?? plan?.removed ?? 0} suppressions ·
                {" "}{plan?.remapped_lines_simulated ?? plan?.remapped_lines ?? 0} remaps
              </Badge>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="pb-2 pr-4 text-left font-medium">À conserver</th>
                    <th className="pb-2 pr-4 text-left font-medium">À supprimer</th>
                    <th className="pb-2 pr-2 text-right font-medium">Lignes remappées</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {homogeneousGroups.map((g, i) => (
                    <tr key={i} className="hover:bg-slate-800/40">
                      <td className="py-2 pr-4">
                        <div className="font-medium text-slate-100">{g.keep_label}</div>
                        <div className="text-xs text-slate-500">{g.keep_id?.slice(0, 8)}…</div>
                      </td>
                      <td className="py-2 pr-4 text-slate-300">
                        {(g.removed_labels || []).map((lbl, j) => (
                          <div key={j} className="text-xs">
                            {lbl} <span className="text-slate-600">({g.removed_ids?.[j]?.slice(0, 8)}…)</span>
                          </div>
                        ))}
                      </td>
                      <td className="py-2 pr-2 text-right text-slate-400">
                        {g.remapped_lines_count ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rien à dédupliquer */}
      {status === "plan_ready" &&
       homogeneousGroups.length === 0 &&
       heterogeneousGroups.length === 0 && (
        <Card className="border-emerald-900/40 bg-emerald-950/20">
          <CardContent className="flex items-center gap-3 p-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <div className="text-sm text-emerald-200">
              Aucun doublon détecté dans le catalogue. Rien à fusionner.
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
